import { Component, OnInit } from '@angular/core';
import { ComponentBase } from 'src/app/main/dynamic/component-base';
import { DynamicEvent } from 'src/app/main/dynamic/dynamic.event';
import { AttributeModel } from 'src/app/main/dynamic/model/attribute.model';

export class TimeSlot {
  attributes: AttributeModel[]; // Lista de atributos para profissionais diferentes
  hour: string;
  selected: boolean = false;

  constructor(attribute: AttributeModel) {
    this.attributes = [attribute];
    this.hour = this.extractHour(attribute);
  }

  addAttribute(attribute: AttributeModel) {
    this.attributes.push(attribute);
  }

  private extractHour(attribute: AttributeModel): string {
    const startTime = attribute.attributes?.find(attr => attr.key === 'Start time')?.value;
    return startTime ? new Date(startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  }
}

interface Day {
  name: string;
  date: Date;
  times: TimeSlot[];
}

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent extends ComponentBase implements OnInit {
  days: Day[] = [];
  selectedItems: TimeSlot[] = [];
  currentWeekIndex = 0;

  availability: AttributeModel[] = [];
  weeks: Day[][] = [];
  showProfessionalModal = false;
  selectedTimeSlot: TimeSlot | null = null;
  selectedDate: string | null = null;
  selectedTime: string | null = null;
  selectedProfessional: string | null = null;

  get isPreviousDisabled(): boolean {
    return this.currentWeekIndex === 0;
  }

  get isNextDisabled(): boolean {
    return this.weeks.length > 0 && this.currentWeekIndex === this.weeks.length - 1;
  }

  ngOnInit() {
    const availabilityItems = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == "items");

    if (availabilityItems) {
      this.availability = availabilityItems.attributes;
      this.processAvailability(this.availability);
      this.showCurrentWeek();
      this.loadPreviouslySelectedItems();
      this.setInitialWeekWithSelection();
    }
  }

  parseDateToLocal(dateTimeString: string): Date {
    return new Date(dateTimeString);
  }

  processAvailability(availability: AttributeModel[]) {
    const groupedByDay: { [key: string]: TimeSlot[] } = {};

    availability.forEach(item => {
      const startTimeAttribute = item.attributes?.find(attr => attr.key === 'Start time');
      if (startTimeAttribute?.value) {
        const date = this.parseDateToLocal(startTimeAttribute.value);
        const dayKey = date.toDateString();
        const hour = new Date(startTimeAttribute.value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        if (!groupedByDay[dayKey]) {
          groupedByDay[dayKey] = [];
        }

        let timeSlot = groupedByDay[dayKey].find(ts => ts.hour === hour);
        if (timeSlot) {
          timeSlot.addAttribute(item);
        } else {
          timeSlot = new TimeSlot(item);
          groupedByDay[dayKey].push(timeSlot);
        }
      }
    });

    const startDate = this.getMondayOfSameWeek(this.parseDateToLocal(availability[0].attributes?.find(attr => attr.key === 'Start time')?.value!));
    const endDate = this.parseDateToLocal(availability[availability.length - 1].attributes?.find(attr => attr.key === 'Start time')?.value!);
    const allDays = this.generateBusinessDays(startDate, endDate);

    allDays.forEach(day => {
      const dayKey = day.date.toDateString();
      day.times = groupedByDay[dayKey] || [];
    });

    this.weeks = [];
    for (let i = 0; i < allDays.length; i += 5) {
      this.weeks.push(allDays.slice(i, i + 5));
    }
  }

  getMondayOfSameWeek(date: Date): Date {
    const dayOfWeek = date.getDay();
    const monday = new Date(date);
    monday.setDate(date.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    return monday;
  }

  generateBusinessDays(startDate: Date, endDate: Date): Day[] {
    const days: Day[] = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        days.push({
          name: currentDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short' }),
          date: new Date(currentDate),
          times: []
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }

  showCurrentWeek() {
    this.days = this.weeks[this.currentWeekIndex] || [];
  }

  previous() {
    if (this.currentWeekIndex > 0) {
      this.currentWeekIndex--;
      this.showCurrentWeek();
    }
  }

  next() {
    if (this.currentWeekIndex < this.weeks.length - 1) {
      this.currentWeekIndex++;
      this.showCurrentWeek();
    }
  }

  today() {
    this.currentWeekIndex = 0;
    this.showCurrentWeek();
  }

  selectTime(day: Day, timeSlot: TimeSlot) {
    if (this.checkViewMode('edit')) {
      this.days.forEach(d => d.times.forEach(t => t.selected = false));
      timeSlot.selected = true;

      if (timeSlot.attributes.length > 1) {
        this.openProfessionalSelection(timeSlot);
      } else if (timeSlot.attributes.length === 1) {
        this.confirmProfessionalSelection(timeSlot.attributes[0]);
      }
    }
  }

  openProfessionalSelection(timeSlot: TimeSlot) {
    this.selectedTimeSlot = timeSlot;
    this.showProfessionalModal = true;
  }

  confirmProfessionalSelection(selectedAttribute: AttributeModel) {
    this.selectedItems = [new TimeSlot(selectedAttribute)];
    this.showProfessionalModal = false;
    this.changedSelection();
  }

  changedSelection() {
    let events: DynamicEvent[] = [];

    let selectedItemsAttr = this.attribute.attributes
      .find(attr => attr.key.toLowerCase() === "selected items");

    if (!selectedItemsAttr) {
      selectedItemsAttr = {
        key: "Selected Items",
        type: "Array",
        attributes: [],
        options: []
      };

      this.attribute.attributes.push(selectedItemsAttr);
    }

    selectedItemsAttr.attributes = this.selectedItems.map(slot => slot.attributes[0]);

    events.push({ name: "autosave", data: "silently" });
    this.emitEvents(events);
  }

  private loadPreviouslySelectedItems() {
    const previousSelectedItems = this.attribute.attributes
      .find(attr => attr.key.toLowerCase() === "selected items");
  
    if (previousSelectedItems && previousSelectedItems.attributes) {
      if (this.checkViewMode('edit')) {
        previousSelectedItems.attributes.forEach(selectedAttr => {
          const matchedTimeSlot = this.findTimeSlot(selectedAttr);
          if (matchedTimeSlot) {
            matchedTimeSlot.selected = true;
            this.selectedItems.push(matchedTimeSlot);
          }
        });
      } else {
        const selectedAttr = previousSelectedItems.attributes[0];
        if (selectedAttr) {
          const startTimeAttr = selectedAttr.attributes?.find(attr => attr.key === 'Start time')?.value;
          this.selectedDate = startTimeAttr ? new Date(startTimeAttr).toLocaleDateString('pt-BR') : null;
          this.selectedTime = startTimeAttr ? new Date(startTimeAttr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
          this.selectedProfessional = this.getProfessionalName(selectedAttr);
        }
      }
    }
  }

  private setInitialWeekWithSelection() {
    const selectedWeekIndex = this.weeks.findIndex(week =>
      week.some(day => day.times.some(time => time.selected))
    );

    if (selectedWeekIndex >= 0) {
      this.currentWeekIndex = selectedWeekIndex;
    }

    this.showCurrentWeek();
  }

  private findTimeSlot(selectedAttr: AttributeModel): TimeSlot | undefined {
    const startTime = selectedAttr.attributes?.find(attr => attr.key === 'Start time')?.value;
    if (startTime) {
      for (const week of this.weeks) {
        for (const day of week) {
          const matchingSlot = day.times.find(
            timeSlot => timeSlot.attributes[0]?.attributes?.find(attr => attr.key === 'Start time')?.value === startTime
          );
          if (matchingSlot) return matchingSlot;
        }
      }
    }
    return undefined;
  }

  getProfessionalName(attribute: AttributeModel): string | null {
    return attribute.attributes.find(attr => attr.key === 'Nome profissional')?.value || null;
  }

  getSelectedProfessionalName(timeSlot: TimeSlot): string | null {
    const selectedAttribute = timeSlot.attributes[0];
    return selectedAttribute?.attributes?.find(attr => attr.key === 'Nome profissional')?.value || null;
  }
}